import { Button, Checkbox, Colors, Icon, Intent } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, showError, showSuccess, useApiEffect, useGridState } from "nsitools-react";
import * as React from "react";

import { ERequestState, InvoiceItemDto, RequestApi } from "../../api";
import { CustomDataTable } from "../../components";
import { useDialog, useGlobalData } from "../../contexts";
import { useApiService, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";

export interface IBillTabProps {
  requestId: number;
  disabled: boolean;
}

export const BillTab: React.FunctionComponent<IBillTabProps> = ({ requestId, disabled }) => {
  const requestApi = useApiService(RequestApi);
  const { t } = useTl();
  const { showDialogPromise } = useDialog();
  const [selectionList, setSelectionList] = React.useState([]);
  const [resendBillLoading, setresendBillLoading] = React.useState(false);
  const [CreditMemoLoading, setCreditMemoLoading] = React.useState(false);
  const { currentLanguage } = useGlobalData();

  const isBilled = React.useMemo(async () => {
    var res = await requestApi.getRequestStatus(requestId);
    return res === ERequestState.BILLED;
  }, [requestApi, requestId]);

  const changeSelected = React.useCallback(
    (id: number) => {
      if (selectionList.find((c) => c === id)) {
        const index = selectionList.findIndex((e) => e === id);
        selectionList.splice(index, 1);
      } else {
        selectionList.push(id);
      }
      setSelectionList([...selectionList]);
    },
    [selectionList]
  );
  const tableState = useGridState<any>({
    serverMode: false,
    pageSize: 150,
    enablePagination: false,
    enableFilter: false,
  });

  const [data, loading, , refresh] = useApiEffect(
    async () => await requestApi.getRequestInvoice(requestId),
    [requestId]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => "",
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: (row: InvoiceItemDto) => (
          <Checkbox
            checked={selectionList.find((c) => c === row.invoiceItemId)}
            onChange={() => changeSelected(row.invoiceItemId)}
            disabled={disabled || !!row.creditMemoBcIdentifier}
          />
        ),
      },
      {
        header: () => t(ETLCodes.Article),
        fieldName: "articleTranslations",
        render: (row: InvoiceItemDto) =>
          row.articleTranslations.find((c) => c.languageId === currentLanguage.languageId)?.text,
      },

      {
        header: () => t(ETLCodes.CreditMemo),
        fieldName: "creditMemoBcIdentifier",
        render: (row: InvoiceItemDto) =>
          row.creditMemoBcIdentifier ? row.creditMemoBcIdentifier : <Icon icon="cross" color={Colors.RED5} />,
      },
    ],
    [changeSelected, currentLanguage.languageId, disabled, selectionList, t]
  );

  React.useEffect(() => {
    if (!loading && tableState.data !== data?.invoiceItems && data) {
      tableState.setData(data?.invoiceItems);
    }
  }, [data, loading, tableState]);

  const CreateCreditMemo = React.useCallback(async () => {
    setCreditMemoLoading(true);
    const dialogResult = await showDialogPromise({
      message: t(ETLCodes.CreditMemoConfirmationMessage),
      title: t(ETLCodes.CreditMemoConfirmationTitle),
    });
    if (dialogResult !== "yes") {
      setCreditMemoLoading(false);
      return;
    }
    try {
      await requestApi.createCreditMemo(requestId, selectionList);
      showSuccess(t(ETLCodes.SaveSuccess));
      refresh();
    } catch {
      showError(t(ETLCodes.Error));
    }
    setSelectionList([]);
    setCreditMemoLoading(false);
  }, [refresh, requestApi, requestId, selectionList, showDialogPromise, t]);

  const ResendBill = React.useCallback(async () => {
    setresendBillLoading(true);
    const dialogResult = await showDialogPromise({
      message: t(ETLCodes.BillResendConfirmationMessage),
      title: t(ETLCodes.Warning),
    });
    if (dialogResult !== "yes") {
      setresendBillLoading(false);
      return;
    }
    try {
      await requestApi.resendInvoice(requestId);
      showSuccess(t(ETLCodes.SaveSuccess));
      refresh();
    } catch {
      showError(t(ETLCodes.Error));
    }
    setresendBillLoading(false);
  }, [refresh, requestApi, requestId, showDialogPromise, t]);

  return (
    <FieldSet
      title={`${t(ETLCodes.Billing)} - ${data && data.invoiceBcIdentifier ? data?.invoiceBcIdentifier : ""}`}
      rightElement={
        <>
          <Button
            icon={<i className="fas fa-money-bill" />}
            minimal
            intent={Intent.PRIMARY}
            text={t(ETLCodes.CreditNote)}
            onClick={CreateCreditMemo}
            loading={CreditMemoLoading}
            disabled={selectionList.length === 0}
          />
          {(!data || !data.invoiceBcIdentifier) && isBilled ? (
            <Button
              icon={<i className="fas fa-envelope-open-text" />}
              minimal
              intent={Intent.PRIMARY}
              text={t(ETLCodes.ResendBill)}
              onClick={ResendBill}
              loading={resendBillLoading}
            />
          ) : null}
        </>
      }
    >
      <CustomDataTable tableState={tableState} columns={columns} loading={loading} />
    </FieldSet>
  );
};
